import type { UploadWidgetConfig } from '@bytescale/upload-widget';

import { generateUUID, getYearMonthPath } from '../utils';
import { t } from '../utils/i18n';
import { apiKey, getSharedLocale, getUploadStyles, sharedStyles, uploadBucket } from './utils';

// GLOBALNA ścieżka uploadu — WSZYSTKIE pliki mają lądować w
//   {bucket}/{orgId}/media/{rok}/{miesiąc}
// (jeden porządek zamiast ścieżek sklejanych ręcznie w każdym komponencie — część miała
// literówki albo niespójny leading slash). getYearMonthPath() → "YYYY/MM".
// Użycie:  options={{ ...getBaseOptions(isDark), path: getMediaUploadPath(organizationId) }}
export const getMediaUploadPath = (organizationId: string | null | undefined) => ({
  folderPath: `/${uploadBucket}/${organizationId}/media/${getYearMonthPath()}`,
  fileName: '{ORIGINAL_FILE_NAME}-' + generateUUID() + '{ORIGINAL_FILE_EXT}',
});

// Function to get upload options with theme support
export const getBaseOptions = (isDark: boolean): Omit<UploadWidgetConfig, 'path'> => ({
  apiKey,
  editor: {
    images: {
      preview: true,
      crop: true,
      cropShape: 'rect' as const,
      allowResizeOnMove: true,
    },
  },
  styles: getUploadStyles(isDark),
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: true,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
});

const sharedEditorBase = {
  preview: true,
  crop: true,
  cropShape: 'rect' as const,
};

export const baseOptions: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      crop: true,
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: true,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
};

export const baseOptionsNoRatio: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: true,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
};

export const baseForBranding: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
};

// Function to get branding options with theme support
export const getBaseForBranding = (isDark: boolean): Omit<UploadWidgetConfig, 'path'> => ({
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
    },
  },
  styles: getUploadStyles(isDark),
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
});

export const baseForGallery: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 8,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: true,
  showRemoveButton: true,
  showFinishButton: true,
};

export const baseForGalleryHotel: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: true,
  },
  maxFileCount: 8,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: true,
  showRemoveButton: true,
  showFinishButton: true,
};

// Function to get gallery hotel options with theme support
export const getBaseForGalleryHotel = (isDark: boolean): Omit<UploadWidgetConfig, 'path'> => ({
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
    },
  },
  styles: getUploadStyles(isDark),
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: true,
    finishBtn: t('upload.finish') || 'Finish',
  },
  maxFileCount: 8,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: true,
  showRemoveButton: true,
  showFinishButton: true,
});

export const baseForUser: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      ...sharedEditorBase,
      allowResizeOnMove: true,
      cropRatio: 1,
      cropShape: 'circ',
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 512 * 512,
  multi: false,
  showRemoveButton: true,
  showFinishButton: true,
};

// Cover image configuration with 16:9 aspect ratio for better display
export const baseForCoverImage: Omit<UploadWidgetConfig, 'path'> = {
  apiKey,
  editor: {
    images: {
      preview: true,
      crop: true,
      cropShape: 'rect' as const,
      allowResizeOnMove: true,
      cropRatio: 16 / 9, // 16:9 aspect ratio for cover images
    },
  },
  styles: sharedStyles,
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
};

// Function to get cover image options with theme support
export const getBaseForCoverImage = (isDark: boolean): Omit<UploadWidgetConfig, 'path'> => ({
  apiKey,
  editor: {
    images: {
      preview: true,
      crop: true,
      cropShape: 'rect' as const,
      allowResizeOnMove: true,
      cropRatio: 16 / 9, // 16:9 aspect ratio for cover images
    },
  },
  styles: getUploadStyles(isDark),
  locale: {
    ...getSharedLocale(),
    cropBtn: t('upload.crop'),
    finishBtnIcon: false,
  },
  maxFileCount: 1,
  maxFileSizeBytes: 6 * 1024 * 1024,
  multi: false,
  showRemoveButton: true,
  showFinishButton: false,
});
